import React, { useState, useEffect, useRef } from 'react'

export interface AutocompleteOption {
  label: string
  value: string
  subtext?: string
}

interface AutocompleteSelectProps {
  value: string
  onChange: (val: string) => void
  options: (string | AutocompleteOption)[]
  placeholder?: string
  required?: boolean
  className?: string
  autoFocus?: boolean
  label?: string
}

export function AutocompleteSelect({
  value,
  onChange,
  options,
  placeholder = 'Select or type name',
  required = false,
  className = '',
  autoFocus = false,
}: AutocompleteSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)

  // Normalize options into AutocompleteOption objects
  const normalizedOptions: AutocompleteOption[] = options
    .filter(Boolean)
    .map((opt) => (typeof opt === 'string' ? { label: opt, value: opt } : opt))

  // Filter options based on input
  const filtered = normalizedOptions.filter(
    (opt) =>
      opt.label &&
      opt.label.trim() &&
      (!value.trim() ||
        opt.label.toLowerCase().includes(value.trim().toLowerCase()) ||
        opt.value.toLowerCase().includes(value.trim().toLowerCase()))
  )

  // Reset highlighted index when options or search term changes
  useEffect(() => {
    setHighlightedIndex(-1)
  }, [value, isOpen])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        setIsOpen(true)
        return
      }
    }

    if (filtered.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev + 1 < filtered.length ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev - 1 >= 0 ? prev - 1 : filtered.length - 1))
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        e.preventDefault()
        onChange(filtered[highlightedIndex].value)
        setIsOpen(false)
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative flex items-center">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          autoFocus={autoFocus}
          className={`w-full pr-8 border-b-2 border-gray-300 dark:border-neutral-700 py-2 px-1 text-gray-900 dark:text-white bg-transparent placeholder-gray-400 focus:outline-none focus:border-[var(--color-cricket-primary)] transition-colors text-base ${className}`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setIsOpen((prev) => !prev)}
          className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 focus:outline-none"
        >
          <svg
            className={`size-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isOpen && filtered.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-[var(--theme-card-bg,#ffffff)] text-[var(--theme-text,#111827)] rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto border border-[var(--theme-border,#e5e7eb)] divide-y divide-[var(--theme-border,#f3f4f6)] animate-in fade-in slide-in-from-top-1">
          {filtered.map((opt, idx) => {
            const isHighlighted = idx === highlightedIndex
            const isSelected = value.toLowerCase() === opt.value.toLowerCase()
            return (
              <button
                key={`${opt.value}-${idx}`}
                type="button"
                onMouseEnter={() => setHighlightedIndex(idx)}
                onClick={() => {
                  onChange(opt.value)
                  setIsOpen(false)
                }}
                className={`w-full text-left px-4 py-3 text-sm font-semibold transition-colors flex items-center justify-between cursor-pointer ${
                  isHighlighted
                    ? 'bg-[var(--color-cricket-primary)]/15 text-[var(--color-cricket-primary)]'
                    : 'text-[var(--theme-text,#111827)] hover:bg-[var(--color-cricket-primary)]/10'
                }`}
              >
                <div className="flex flex-col">
                  <span>{opt.label}</span>
                  {opt.subtext && (
                    <span className="text-[11px] font-normal text-gray-400 dark:text-gray-400">
                      {opt.subtext}
                    </span>
                  )}
                </div>
                {isSelected && (
                  <span className="size-2 rounded-full bg-[var(--color-cricket-primary)]" />
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

